import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, ChevronRight, FileText, RefreshCw, X } from 'lucide-react'
import { usePermissions } from '../../admin/primitives.js'
import { estPageCourante } from '../../shell/menu-contextuel.js'
import { Link, usePath } from '../../router.js'
import { EVENEMENT_SONDE_ECRITE } from './commun.js'
import { cheminAppareil, cheminSalle } from './fiches/modele.js'
import { ecrireSonde } from './registre/ecrire.js'
import {
  aDuDetail, cheminActualiser, cibleValide, cleCible, lireResultat, messageEchecActualiser,
  type CibleActualisation, type ResultatActualisation,
} from './actualiser-modele.js'
import './actualiser.css'

/**
 * « ACTUALISER » UN APPAREIL, UNE SALLE, UNE ALERTE (25/10/2026).
 *
 * Demande d'Hugo : Sonde relance tout de suite les relevés de la cible et
 * répond par une phrase (CONTRAT-GESTION §6). Le geste existe d'abord dans
 * sa page (fiche appareil, fiche salle, alertes d'un incident, volet d'une
 * alerte du centre d'activité), puis dans les menus clic droit, par la même
 * route du relais :
 *
 *   POST devices/:uid/refresh · zones/:code/refresh · alerts/:id/refresh
 *
 * Droit : `sonde.read`, sans step-up ; permis en Lecture seule ; aucune
 * ligne au journal de Gestion (décisions d'Hugo du 25/10/2026 ; la trace est
 * l'audit de Sonde). Gestion ne fabrique RIEN d'autre : ni notification, ni
 * entrée du centre d'activité. La réponse s'affiche dans une carte en bas à
 * droite, sans bloquer l'écran, puis les vues de Sonde se relisent.
 */

export type { CibleActualisation } from './actualiser-modele.js'

interface Carte {
  id: number
  cle: string
  cible: CibleActualisation
  etat: 'attente' | 'fait' | 'echec'
  resultat: ResultatActualisation | null
  erreur: string | null
}

/* ─── la pile des cartes, et les actualisations en cours (une par cible) ─── */

const MAX_CARTES = 4
let cartes: Carte[] = []
let enCours: ReadonlySet<string> = new Set()
let compteur = 0
const abonnes = new Set<() => void>()
const prevenir = () => abonnes.forEach((f) => f())
const abonner = (f: () => void) => { abonnes.add(f); return () => { abonnes.delete(f) } }

function poserCartes(c: Carte[]): void { cartes = c; prevenir() }
function poserEnCours(cle: string, oui: boolean): void {
  const s = new Set(enCours)
  if (oui) s.add(cle); else s.delete(cle)
  enCours = s
  prevenir()
}

export const fermerCarte = (id: number): void => poserCartes(cartes.filter((c) => c.id !== id))
/** À la déconnexion (la pile se démonte) : le compte suivant ne voit rien du précédent. */
const viderCartes = (): void => poserCartes([])

/** Lu à la construction d'un menu : l'entrée est grisée pendant l'actualisation. */
export const actualisationEnCours = (c: CibleActualisation): boolean => enCours.has(cleCible(c))

export function useActualisationEnCours(c: CibleActualisation): boolean {
  const cle = cleCible(c)
  return useSyncExternalStore(abonner, () => enCours.has(cle), () => false)
}

/**
 * Lance l'actualisation et rend vrai si Sonde a répondu. Une seule à la fois
 * par cible ; la carte précédente de la même cible est remplacée.
 */
export async function actualiser(cible: CibleActualisation): Promise<boolean> {
  const cle = cleCible(cible)
  if (!cibleValide(cible) || enCours.has(cle)) return false
  const id = ++compteur
  // Seules les cartes FINIES cèdent la place : une actualisation en cours garde la sienne.
  let pile = cartes.filter((c) => c.cle !== cle)
  while (pile.length >= MAX_CARTES) {
    const finie = pile.findIndex((c) => c.etat !== 'attente')
    if (finie < 0) break
    pile = pile.filter((_, i) => i !== finie)
  }
  poserCartes([...pile, { id, cle, cible, etat: 'attente', resultat: null, erreur: null }])
  poserEnCours(cle, true)
  const finir = (m: Pick<Carte, 'etat' | 'resultat' | 'erreur'>) => poserCartes(cartes.map((c) => (c.id === id ? { ...c, ...m } : c)))
  try {
    const e = await ecrireSonde('POST', cheminActualiser(cible), {}, { rien: 'rien n’a été actualisé' })
    if (!e.ok) {
      finir({ etat: 'echec', resultat: null, erreur: messageEchecActualiser(e.status, e.data, e.message) })
      return false
    }
    const r = lireResultat(cible.kind, e.data)
    finir(r
      ? { etat: 'fait', resultat: r, erreur: null }
      : { etat: 'echec', resultat: null, erreur: 'Réponse inattendue de Sonde : rien n’est affiché plutôt qu’un état faux.' })
    // Les relevés ont avancé : les vues de Sonde ouvertes se relisent.
    window.dispatchEvent(new Event(EVENEMENT_SONDE_ECRITE))
    return true
  } finally {
    poserEnCours(cle, false)
  }
}

/* ─── le bouton des pages ─── */

/**
 * « Actualiser » (fiche appareil, alertes), « Actualiser la salle » (fiche
 * salle). Absent sans `sonde.read`. `apres` : relire ce que la page tient
 * hors de Sonde (le centre d'activité).
 */
export function BoutonActualiser({ cible, libelle = 'Actualiser', petit = false, apres }: {
  cible: CibleActualisation; libelle?: string; petit?: boolean; apres?: () => void
}) {
  const { permissions } = usePermissions()
  const occupe = useActualisationEnCours(cible)
  if (!permissions.includes('sonde.read') || !cibleValide(cible)) return null
  // aria-disabled plutôt que disabled : le focus du clavier reste sur le bouton pendant l'attente.
  return (
    <button
      type="button"
      className={`lc-btn lc-btn--ghost${petit ? ' lc-btn--sm' : ''} sv-actu-bouton`}
      aria-disabled={occupe}
      title="Sonde relance tout de suite ses relevés et dit ce qu’elle voit. Aucune notification."
      onClick={(e) => { e.stopPropagation(); if (!occupe) void actualiser(cible).then((ok) => { if (ok) apres?.() }) }}
    >
      <RefreshCw size={petit ? 13 : 15} aria-hidden="true" className={occupe ? 'sv-actu-tourne' : undefined} />
      {occupe ? 'Actualisation…' : libelle}
    </button>
  )
}

/* ─── les cartes ─── */

function Bloc({ titre, lignes }: { titre: string; lignes: string[] }) {
  if (lignes.length === 0) return null
  return (
    <div className="sv-actu__bloc">
      <p className="sv-actu__bloc-titre">{titre}</p>
      <ul>{lignes.map((l, i) => <li key={i}>{l}</li>)}</ul>
    </div>
  )
}

function Detail({ r }: { r: ResultatActualisation }) {
  return (
    <div className="sv-actu__detail">
      {r.alerte ? <Bloc titre="L’alerte" lignes={[`${r.alerte.titre} — ${r.alerte.etat}${r.alerte.fermeture ? `. ${r.alerte.fermeture}` : ''}`]} /> : null}
      {r.changement ? <Bloc titre="État pendant l’actualisation" lignes={[r.changement]} /> : null}
      {r.comptes ? <p className="sv-actu__comptes">{r.comptes}</p> : null}
      <Bloc titre="Absents ou changés" lignes={r.appareils} />
      <Bloc titre="Témoins" lignes={r.temoins.map((t) => `${t.libelle} : ${t.texte}`)} />
      <Bloc titre="Alertes de l’appareil" lignes={r.alertes.map((a) => `${a.titre} : ${a.fermeture}`)} />
      <Bloc titre="Relevés" lignes={r.releves} />
      <Bloc titre="Sources" lignes={r.sources.map((s) => `${s.libelle} : ${s.etat}`)} />
    </div>
  )
}

const ficheDe = (c: CibleActualisation): string | null =>
  c.kind === 'device' ? cheminAppareil(c.uid) : c.kind === 'zone' ? cheminSalle(c.code) : null

function CarteActualisation({ c }: { c: Carte }) {
  // Relu à chaque navigation : « Ouvrir la fiche » disparaît sur la fiche elle-même.
  usePath()
  const [ouvert, setOuvert] = useState(false)
  const [survol, setSurvol] = useState(false)
  // Une carte finie se retire seule au bout d'une minute, sauf détail ouvert ou pointeur dessus.
  useEffect(() => {
    if (c.etat === 'attente' || ouvert || survol) return
    const t = setTimeout(() => fermerCarte(c.id), 60_000)
    return () => clearTimeout(t)
  }, [c.etat, c.id, ouvert, survol])
  const r = c.resultat
  const fiche = ficheDe(c.cible)
  return (
    <section
      className={`sv-actu sv-actu--${c.etat}${r?.limite ? ' sv-actu--limite' : ''}`}
      aria-label={`Actualisation de ${c.cible.nom}`}
      onPointerEnter={() => setSurvol(true)}
      onPointerLeave={() => setSurvol(false)}
    >
      <div className="sv-actu__tete">
        <RefreshCw size={14} aria-hidden="true" className={c.etat === 'attente' ? 'sv-actu-tourne' : undefined} />
        <span className="sv-actu__cible">{c.etat === 'attente' ? `Actualisation de ${c.cible.nom}…` : c.cible.nom}</span>
        <button type="button" className="sv-actu__fermer" aria-label="Fermer" onClick={() => fermerCarte(c.id)}><X size={14} aria-hidden="true" /></button>
      </div>
      {c.etat === 'attente' ? <p className="sv-actu__doux">Sonde relance ses relevés et attend leur réponse.</p> : null}
      {c.etat === 'echec' ? <p className="sv-actu__phrase">{c.erreur}</p> : null}
      {r ? (
        <>
          <p className="sv-actu__phrase">{r.phrase}</p>
          {r.conseil ? <p className="sv-actu__conseil">{r.conseil}</p> : null}
          <div className="sv-actu__pied">
            {aDuDetail(r) ? (
              <button type="button" className="sv-actu__plus" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
                {ouvert ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                {ouvert ? 'Masquer le détail' : 'Voir le détail'}
              </button>
            ) : null}
            {fiche && !estPageCourante(fiche) ? <Link className="sv-actu__plus" href={fiche}><FileText size={14} aria-hidden="true" /> Ouvrir la fiche</Link> : null}
          </div>
          {ouvert ? <Detail r={r} /> : null}
        </>
      ) : null}
    </section>
  )
}

/**
 * La pile des cartes, montée UNE fois par le shell (AppShell.tsx) pour un
 * compte connecté, en portail dans <body> comme les toasts. Toujours rendue,
 * même vide : la région annoncée existe avant la première carte. Les toasts
 * du même coin montent au-dessus d'elle (`--sv-actu-h`, admin.css).
 */
export function ActualisationsHote() {
  const liste = useSyncExternalStore(abonner, () => cartes, () => cartes)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const racine = document.documentElement
    const ro = new ResizeObserver(() => {
      const h = el.offsetHeight
      racine.style.setProperty('--sv-actu-h', h > 0 ? `${h + 8}px` : '0px')
    })
    ro.observe(el)
    return () => { ro.disconnect(); racine.style.removeProperty('--sv-actu-h'); viderCartes() }
  }, [])
  if (typeof document === 'undefined') return null
  return createPortal(
    <div ref={ref} className="sv-actu-pile" role="status" aria-live="polite">
      {liste.map((c) => <CarteActualisation key={c.id} c={c} />)}
    </div>,
    document.body,
  )
}
